<template>
  <div class="text-gray-800 tabular-nums" :class="embedded ? 'min-w-[1180px] overflow-x-auto' : 'h-full min-w-[1180px] overflow-y-auto'">
    <div :class="embedded ? 'flex flex-col gap-4' : 'px-6 pt-6 pb-[120px] flex flex-col gap-4'">
      <div v-if="!loading && impactModel && !embedded" class="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between gap-3">
          <div>
            <h3 class="m-0 text-[13px] font-semibold text-gray-900">Modeled impact if these constraints are cleared</h3>
            <p class="m-0 mt-0.5 text-[11px] text-gray-500">Across all {{ filteredConstraints.length.toLocaleString() }} constraints matching the filters · {{ modeledCount.toLocaleString() }} modeled against their practice's latest metric reading.</p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span class="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800" :title="impactBasisTitle">{{ impactBasisLabel }}</span>
            <button type="button" class="text-[11px] text-blue-700 hover:underline" @click="showAssumptions = !showAssumptions">{{ showAssumptions ? 'Hide' : 'How this is modeled' }}</button>
          </div>
        </div>

        <div class="grid divide-x divide-gray-100" :style="{ gridTemplateColumns: `repeat(${impactCards.length}, minmax(0, 1fr))` }">
          <div v-for="k in impactCards" :key="k.initiative" class="px-4 py-3">
            <div class="flex items-center gap-1.5">
              <span class="inline-block h-2 w-2 rounded-full" :style="{ background: k.color }"></span>
              <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-600">{{ k.label }}</span>
              <span class="ml-auto text-[10px] uppercase tracking-wide" :class="k.basis === 'tested' ? 'text-green-700' : 'text-gray-400'">{{ k.basis }}</span>
            </div>
            <div class="mt-1 text-xl font-semibold leading-tight text-gray-900">+{{ fmtInt(k.total) }}</div>
            <div class="text-[11px] text-gray-500">{{ k.unit }} · all in view</div>
            <div class="mt-1.5 text-[12px] text-gray-700">
              <span class="font-semibold">+{{ k.ptsNetwork.toFixed(1) }} pts</span> network · <span class="font-semibold">+{{ k.ptsPractices.toFixed(1) }} pts</span> at those practices
            </div>
            <div class="mt-0.5 text-[11px] text-gray-400">{{ k.count }} constraints · {{ k.practices }} practices · {{ k.rate != null ? (k.rate * 100).toFixed(1) + '% now' : 'no reading' }}</div>
            <div class="mt-2 rounded-md border px-2 py-1.5" :class="k.priCount ? 'border-amber-200 bg-amber-50' : 'border-gray-100 bg-gray-50'">
              <div class="flex items-baseline gap-1.5">
                <span class="text-[12px]" :class="k.priCount ? 'text-amber-500' : 'text-gray-300'">★</span>
                <span class="text-[15px] font-semibold" :class="k.priCount ? 'text-amber-900' : 'text-gray-400'">+{{ fmtInt(k.priTotal) }}</span>
                <span class="text-[11px]" :class="k.priCount ? 'text-amber-800' : 'text-gray-400'">prioritized</span>
              </div>
              <div class="text-[11px]" :class="k.priCount ? 'text-amber-800' : 'text-gray-400'">{{ k.priCount }} constraints · +{{ k.priPtsNetwork.toFixed(1) }} pts network</div>
            </div>
          </div>
        </div>

        <div v-if="showAssumptions" class="px-4 py-3 border-t border-gray-200 bg-slate-50 text-[12px] text-gray-700">
          <div class="grid grid-cols-2 gap-x-8 gap-y-1.5">
            <div><span class="font-semibold">Lift per cleared constraint:</span> {{ impactLiftSummary }}</div>
            <div><span class="font-semibold">Applied to</span> the practice's latest rate and patient count, and capped at the constraint's share of the practice's gap to target, split by severity (high 3, medium 2, low 1) across its open constraints on that initiative.</div>
            <div><span class="font-semibold">Network pts</span> divide the gain by every attributed patient on that metric; <span class="font-semibold">pts at those practices</span> divide it by the patients at the practices with a modeled constraint.</div>
            <div><span class="font-semibold">Not modeled:</span> {{ impactNotModeled }}</div>
          </div>
        </div>
      </div>

      <div v-if="!loading && engagements.length && !embedded" class="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="px-4 py-2.5 border-b border-gray-200 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 class="m-0 text-[13px] font-semibold text-gray-900">Engagements at practices with constraints</h3>
            <p class="m-0 mt-0.5 text-[11px] text-gray-500">
              {{ engagementStats.priPractices }} practices with a prioritized constraint in view ·
              {{ engagementStats.priMeetings.toLocaleString() }} meetings in range<template v-if="engagementStats.priPractices"> · {{ engagementStats.priPerPracticeMonth.toFixed(1) }} per practice per month</template>
              · all {{ engagementStats.allPractices }} practices in view: {{ engagementStats.allMeetings.toLocaleString() }} meetings, {{ engagementStats.allPerPracticeMonth.toFixed(1) }} per practice per month
            </p>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <div class="inline-flex rounded border border-gray-300 overflow-hidden text-[12px]">
              <button
v-for="o in ENG_GRAINS" :key="o[0]" type="button" class="px-2 py-1 border-l border-gray-200 first:border-l-0"
                      :class="engGrain === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'" @click="engGrain = o[0]">{{ o[1] }}</button>
            </div>
            <div class="inline-flex rounded border border-gray-300 overflow-hidden text-[12px]">
              <button
v-for="o in ENG_RANGES" :key="o[0]" type="button" class="px-2 py-1 border-l border-gray-200 first:border-l-0"
                      :class="engRange === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'" @click="engRange = o[0]">{{ o[1] }}</button>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Type</span>
              <SimpleMultiSelect v-model="engTypes" :options="engTypeOptions" placeholder="None" class="w-44" />
            </div>
          </div>
        </div>
        <div class="px-2 pt-2 pb-1 h-[260px]">
          <Chart_V5 :chart_data="engagementChart" :chart_config="engagementChartConfig" override-height="h-full" />
        </div>
        <p class="m-0 px-4 pb-2.5 text-[11px] text-gray-500">Blue: engagements at every practice with a constraint in view. Amber: engagements at the subset of those practices that have a prioritized constraint. Both on the same axis; follows the filters.</p>
      </div>

      <div class="bg-white border border-gray-200 rounded-lg">
        <div class="px-4 py-2 bg-blue-50 border-b border-blue-100 rounded-t-lg flex items-center justify-between gap-3">
          <h3 class="m-0 text-[13px] font-semibold text-blue-900">
            Open constraints <span v-if="!loading" class="font-normal text-blue-700/70">— {{ filteredConstraints.length }} of {{ constraints.length }}</span>
          </h3>
          <div class="flex items-center gap-3">
            <div v-if="embedded && !loading" class="flex items-center gap-1.5">
              <span class="text-[10px] font-semibold uppercase tracking-[0.05em] text-blue-900/60">Sort</span>
              <div class="inline-flex overflow-hidden rounded border border-blue-200 text-[11px]">
                <button
                  v-for="o in EMBEDDED_SORTS"
                  :key="o[0]"
                  type="button"
                  class="px-2 py-0.5"
                  :class="sortCol === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-blue-800 hover:bg-blue-100'"
                  @click="applyEmbeddedSort(o[0])"
                >{{ o[1] }}</button>
              </div>
            </div>
            <button v-if="!loading && filtersChanged" type="button" class="text-[11px] text-blue-700 hover:underline" @click="resetFilters">Reset filters</button>
          </div>
        </div>
        <div v-if="!loading" class="px-4 py-3 border-b border-gray-200 bg-white grid grid-cols-3 gap-x-6 gap-y-2">
          <div v-for="f in FILTERS" :key="f.key" class="grid grid-cols-[120px_1fr] items-center gap-2">
            <span class="text-right text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">{{ f.label }}</span>
            <SimpleMultiSelect
              v-model="filters[f.key]"
              :options="filterOptions[f.key]"
              :searchable="f.searchable"
              placeholder="None"
              class="w-full"
            />
          </div>
          <div class="grid grid-cols-[120px_1fr] items-center gap-2">
            <span class="text-right text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Scope</span>
            <div class="flex rounded border border-gray-300 overflow-hidden text-[12px]">
              <button
                v-for="o in SCOPE_OPTIONS"
                :key="o[0]"
                type="button"
                class="flex-1 px-2 py-1 border-l border-gray-200 first:border-l-0"
                :class="scopeFilter === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                @click="scopeFilter = o[0]"
              >{{ o[1] }}</button>
            </div>
          </div>
          <div class="grid grid-cols-[120px_1fr] items-center gap-2">
            <span class="text-right text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Solved elsewhere</span>
            <div class="flex rounded border border-gray-300 overflow-hidden text-[12px]">
              <button
                v-for="o in SOLVED_OPTIONS"
                :key="o[0]"
                type="button"
                class="flex-1 px-2 py-1 border-l border-gray-200 first:border-l-0"
                :class="solvedFilter === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                @click="solvedFilter = o[0]"
              >{{ o[1] }}</button>
            </div>
          </div>
          <div class="grid grid-cols-[120px_1fr] items-center gap-2">
            <span class="text-right text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Priority</span>
            <div class="flex rounded border border-gray-300 overflow-hidden text-[12px]">
              <button
                v-for="o in PRIORITY_OPTIONS"
                :key="o[0]"
                type="button"
                class="flex-1 px-2 py-1 border-l border-gray-200 first:border-l-0"
                :class="priorityFilter === o[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                @click="priorityFilter = o[0]"
              >{{ o[1] }}</button>
            </div>
          </div>
        </div>
        <div v-if="!loading && selectedIds.size" class="px-4 py-2 border-b border-amber-200 bg-amber-50 flex items-center gap-3 text-[12px]">
          <span class="font-semibold text-amber-900">{{ selectedIds.size.toLocaleString() }} selected</span>
          <button
            v-if="selectedIds.size < filteredConstraints.length"
            type="button"
            class="text-blue-700 hover:underline"
            @click="selectAllFiltered"
          >Select all {{ filteredConstraints.length.toLocaleString() }} matching the filters</button>
          <div class="ml-auto flex items-center gap-2">
            <button type="button" class="rounded-md bg-amber-500 px-3 py-1 font-medium text-white hover:bg-amber-600 disabled:opacity-50" :disabled="bulkSaving" @click="bulkPriority(true)">★ Prioritize</button>
            <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50" :disabled="bulkSaving" @click="bulkPriority(false)">Remove priority</button>
            <button type="button" class="px-2 py-1 text-gray-600 hover:underline" @click="clearSelection">Clear</button>
          </div>
        </div>

        <div v-if="loading" class="py-16 text-center text-[13px] text-gray-500">Loading…</div>

        <template v-else>
          <div ref="scroller" class="max-h-[calc(100vh-260px)] overflow-y-auto">
          <table class="w-full table-fixed border-collapse">
            <colgroup>
              <col style="width: 36px">
              <col style="width: 44px">
              <col>
              <col style="width: 190px">
              <col style="width: 80px">
              <col style="width: 64px">
              <col style="width: 120px">
              <col style="width: 110px">
              <col style="width: 80px">
              <col style="width: 110px">
              <col style="width: 110px">
              <col style="width: 70px">
              <col style="width: 84px">
              <col style="width: 72px">
              <col style="width: 140px">
            </colgroup>
            <thead>
              <tr class="text-[11px] font-semibold text-gray-500 align-bottom">
                <th class="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_#e5e7eb] pl-4 pr-1 py-2 text-left">
                  <input type="checkbox" :checked="pageAllSelected" :indeterminate.prop="pageSomeSelected" title="Select this page" @change="togglePageSelection">
                </th>
                <th
                  v-for="(col, ci) in COLUMNS"
                  :key="col.key"
                  class="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_#e5e7eb] py-2 text-left cursor-pointer select-none hover:bg-gray-50 hover:text-gray-700"
                  :class="[ci === 0 ? 'px-1' : 'px-2', ci === COLUMNS.length - 1 ? 'pr-4' : '', sortCol === col.key ? 'text-blue-700' : '']"
                  :title="col.help"
                  @click="clickHeader(col.key)"
                >{{ col.label }}<span v-if="sortCol === col.key" class="text-blue-600">{{ sortDir === 'asc' ? ' ▲' : ' ▾' }}</span></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="c in pageRows" :key="c.id">
              <tr
                class="border-b border-gray-100 align-top cursor-pointer hover:bg-gray-50"
                :class="[expandedId === c.id ? 'bg-blue-50/40' : '', c.prioritized ? 'shadow-[inset_3px_0_0_#f59e0b]' : '']"
                @click="expandedId = expandedId === c.id ? null : c.id"
              >
                <td class="pl-4 pr-1 py-1.5" @click.stop>
                  <input type="checkbox" :checked="selectedIds.has(c.id)" @change="toggleSelected(c.id)">
                </td>
                <td class="px-1 py-1" @click.stop>
                  <button
                    type="button"
                    class="text-[15px] leading-none"
                    :class="c.prioritized ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'"
                    :title="priorityTitle(c)"
                    @click="togglePriority(c)"
                  >★</button>
                </td>
                <td class="pl-1 pr-2 py-1.5">
                  <div class="flex items-start gap-1.5 min-w-0">
                    <span class="w-3 flex-none pt-0.5 text-[10px] text-gray-400">{{ expandedId === c.id ? '▾' : '▸' }}</span>
                    <span class="min-w-0 break-words text-[13px] font-medium leading-snug text-gray-900">{{ c.title || '(untitled)' }}</span>
                  </div>
                </td>
                <td class="px-2 py-1.5 text-[13px] text-gray-700">
                  <span class="block truncate" :title="c.practice">{{ c.practice || '—' }}</span>
                  <span v-if="embedded" class="block text-[10px] font-medium" :class="nextMeetingClass(c)">{{ nextMeetingLabel(c) }}</span>
                </td>
                <td class="px-2 py-1.5 text-right text-[12px] tabular-nums" :class="attributedOf(c) != null ? 'text-gray-700' : 'text-gray-300'">{{ attributionLoading ? '…' : (attributedOf(c) != null ? attributedOf(c).toLocaleString() : '—') }}</td>
                <td class="px-2 py-1.5">
                  <span
                    v-if="c.manager"
                    class="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full text-[10px] font-bold text-white"
                    :style="{ background: avatarColor(c.manager) }"
                    :title="c.manager"
                  >{{ initialsOf(c.manager) }}</span>
                  <span v-else class="text-[12px] text-gray-300" title="Unassigned">—</span>
                </td>
                <td class="px-2 py-1.5 text-[12px] text-gray-600 truncate">{{ c.initiative ? titleize(c.initiative) : '—' }}</td>
                <td class="px-2 py-1.5 text-[12px] text-gray-600 truncate">{{ c.gap_type ? titleize(c.gap_type) : '—' }}</td>
                <td class="px-2 py-1.5 text-[12px] font-semibold uppercase" :class="SEVERITY_CLS[c.severity] || 'text-gray-400'">{{ c.severity || '—' }}</td>
                <td class="px-2 py-1.5 text-[12px] text-gray-600 whitespace-nowrap">{{ fmtDate(c.raised) }}</td>
                <td class="px-2 py-1.5 text-[12px] text-gray-600 whitespace-nowrap">{{ fmtDate(c.last_activity) }}</td>
                <td class="px-2 py-1.5 text-[12px] tabular-nums" :class="openActions(c) ? 'text-gray-900' : 'text-amber-700'" :title="actionsTitle(c)">{{ openActions(c) }}</td>
                <td class="px-2 py-1.5">
                  <span
                    v-if="c.solvability"
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    :class="POWER_CLS[c.solvability.enabler_power]"
                  >{{ c.solvability.enabler_power }}</span>
                  <span v-else class="text-gray-300 text-[12px]">—</span>
                </td>
                <td class="px-2 py-1.5 text-[12px] capitalize" :class="EFFORT_CLS[c.solvability && c.solvability.effort] || 'text-gray-300'">{{ c.solvability ? c.solvability.effort : '—' }}</td>
                <td class="px-2 pr-4 py-1.5">
                  <span
                    v-if="resolvedFor(c).length"
                    class="inline-flex items-center whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-800"
                  >Solved elsewhere</span>
                </td>
              </tr>
              <tr v-if="expandedId === c.id" class="border-b border-gray-200 bg-slate-50">
                <td :colspan="COLUMNS.length + 1" class="pl-9 pr-4 py-4">
                  <ConstraintDetail :constraint="c" :impact-model="impactModel" />
                </td>
              </tr>
              </template>
            </tbody>
          </table>
          <div v-if="!filteredConstraints.length" class="px-4 py-8 text-center text-[13px] text-gray-500">No open constraints.</div>
          </div>
          <div v-if="filteredConstraints.length" class="px-4 py-2 border-t border-gray-200 flex items-center justify-between text-[12px] text-gray-600">
            <span>{{ pageStart + 1 }}–{{ pageEnd }} of {{ filteredConstraints.length.toLocaleString() }}</span>
            <div class="flex items-center gap-2">
              <button type="button" class="px-2.5 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed" :disabled="page === 1" @click="goPage(page - 1)">Previous</button>
              <span>Page {{ page }} of {{ pageCount }}</span>
              <button type="button" class="px-2.5 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed" :disabled="page === pageCount" @click="goPage(page + 1)">Next</button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import { setPriority, setPriorityBulk } from '@/services/api/engagementGraph';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import ConstraintDetail from '@/components/Transform/ConstraintDetail.vue';
import { titleize } from '@/components/ConfigurablePageComponents/practiceWorkHelpers.js';

const GAP_TYPES = ['knowledge', 'capacity', 'ownership', 'motivation', 'patient_engagement', 'third_party'];
const NONE = '__none__';
const DEFAULT_EXCLUDED = { gap_type: new Set(['patient_engagement', 'third_party']), power: new Set(['no', NONE]) };
const SOLVED_OPTIONS = [['all', 'All'], ['yes', 'Yes'], ['no', 'No']];
const ENG_GRAINS = [['day', 'Day'], ['week', 'Week'], ['month', 'Month']];
const ENG_RANGES = [[30, '30d'], [90, '90d'], [180, '6 mo'], [365, '12 mo']];
const DAY_MS = 24 * 3600 * 1000;
const isoDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseDay = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const bucketStart = (d, grain) => {
  if (grain === 'month') return new Date(d.getFullYear(), d.getMonth(), 1);
  if (grain === 'week') { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const PRIORITY_OPTIONS = [['all', 'All'], ['yes', 'Prioritized'], ['no', 'Not']];
const FILTERS = [
  { key: 'initiative', label: 'Initiative', value: c => c.initiative, width: 'w-40', noneLabel: 'No initiative' },
  { key: 'manager', label: 'Team Member', value: c => c.manager, width: 'w-44', searchable: true, rawLabel: true, noneLabel: 'Unassigned' },
  { key: 'practice', label: 'Practice', value: c => c.practice, width: 'w-52', searchable: true, rawLabel: true, noneLabel: 'No practice' },
  { key: 'gap_type', label: 'Gap', value: c => c.gap_type, width: 'w-40', order: GAP_TYPES, noneLabel: 'No gap type' },
  { key: 'severity', label: 'Severity', value: c => c.severity, width: 'w-32', order: ['high', 'medium', 'low'], noneLabel: 'No severity' },
  { key: 'power', label: 'Enabler', value: c => c.solvability && c.solvability.enabler_power, width: 'w-32', order: ['fully', 'partly', 'no'], noneLabel: 'Not tagged' },
  { key: 'effort', label: 'Effort', value: c => c.solvability && c.solvability.effort, width: 'w-32', order: ['low', 'medium', 'high'], noneLabel: 'Not tagged' },
];
const PAGE_SIZE = 100;
const SCOPE_OPTIONS = [['all', 'All'], ['clinician', 'Clinician Specific']];
const AVATAR_COLORS = ['#2563eb', '#0d9488', '#7c3aed', '#db2777', '#ea580c', '#059669', '#4f46e5', '#b45309'];
const initialsOf = n => (n || '?').replace(/\s*-\s*.*$/, '').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
const avatarColor = n => {
  let h = 0;
  for (const ch of String(n || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };
const SEVERITY_CLS = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-gray-500' };
const POWER_RANK = { fully: 0, partly: 1, no: 2 };
const EFFORT_RANK = { low: 0, medium: 1, high: 2 };
const POWER_CLS = { fully: 'bg-green-50 text-green-800 border border-green-200', partly: 'bg-amber-50 text-amber-800 border border-amber-200', no: 'bg-gray-100 text-gray-600 border border-gray-200' };
const EFFORT_CLS = { low: 'text-green-700', medium: 'text-amber-700', high: 'text-red-600' };
const ASC_DEFAULT = new Set(['title', 'practice', 'manager', 'initiative', 'gap_type', 'severity', 'power', 'effort', 'upcoming']);
const EMBEDDED_SORTS = [['upcoming', 'Upcoming'], ['severity', 'Severity']];
const COLUMNS = [
  { key: 'priority', label: '★', help: 'Prioritized by a manager. Click the star on a row to set or clear it.' },
  { key: 'title', label: 'Constraint' },
  { key: 'practice', label: 'Practice' },
  { key: 'attributed', label: 'Attributed', help: 'Attributed lives at the practice this year (EMPI contract cohort).' },
  { key: 'manager', label: 'Team', help: 'The practice\'s team member (PTM or equivalent). Hover for the name.' },
  { key: 'initiative', label: 'Initiative' },
  { key: 'gap_type', label: 'Gap' },
  { key: 'severity', label: 'Severity' },
  { key: 'raised', label: 'Raised' },
  { key: 'last_activity', label: 'Last activity', help: 'Most recent meeting where the tracker recorded this constraint.' },
  { key: 'covered', label: 'Actions', help: 'Open action items linked to this constraint.' },
  { key: 'power', label: 'Enabler', help: 'How far the enabler can solve this itself: fully, partly or not at all.' },
  { key: 'effort', label: 'Effort', help: 'Low: days, one owner. Medium: weeks, coordinated work. High: months or new capacity.' },
  { key: 'solved', label: 'Solved elsewhere', help: 'A practice resolved the same obstacle, and the fix was checked to carry over.' },
];
export default {
  name: 'TransformOps',
  components: { SimpleMultiSelect, Chart_V5, ConstraintDetail },
  props: {
    entity_id: { type: Number, default: null },
    sectionId: { type: String, default: null },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      constraints: [],
      expandedId: null,
      impactModel: null,
      showAssumptions: false,
      selectedIds: new Set(),
      bulkSaving: false,
      priorityFilter: 'all',
      attribution: {},
      engagements: [],
      engGrain: 'week',
      engRange: 180,
      engTypes: [],
      attributionLoading: false,
      filters: Object.fromEntries(FILTERS.map(f => [f.key, []])),
      scopeFilter: 'all',
      solvedFilter: 'all',
      page: 1,
      sortCol: 'severity',
      sortDir: 'asc',
      COLUMNS, FILTERS, SOLVED_OPTIONS, PRIORITY_OPTIONS, ENG_GRAINS, ENG_RANGES, SEVERITY_CLS, SCOPE_OPTIONS, POWER_CLS, EFFORT_CLS, EMBEDDED_SORTS,
    };
  },
  computed: {
    embedded() {
      return this.sectionId != null;
    },
    filterOptions() {
      const out = {};
      for (const f of FILTERS) {
        const present = new Set(this.constraints.map(c => f.value(c) || NONE));
        const vals = [...present].filter(v => v !== NONE);
        const rank = v => (f.order ? (f.order.indexOf(v) + 1 || 99) : 0);
        vals.sort((x, y) => rank(x) - rank(y) || String(x).localeCompare(String(y)));
        const opts = vals.map(v => ({ value: v, label: f.rawLabel ? v : titleize(v) }));
        if (present.has(NONE)) opts.push({ value: NONE, label: f.noneLabel });
        out[f.key] = opts;
      }
      return out;
    },
    defaultFilters() {
      return Object.fromEntries(FILTERS.map(f => {
        const opts = this.filterOptions[f.key].map(o => o.value);
        const excl = DEFAULT_EXCLUDED[f.key];
        const kept = excl ? opts.filter(v => !excl.has(v)) : opts;
        return [f.key, kept.length ? kept : opts];
      }));
    },
    filtersChanged() {
      if (this.scopeFilter !== 'all' || this.solvedFilter !== 'all' || this.priorityFilter !== 'all') return true;
      const d = this.defaultFilters;
      return FILTERS.some(f => this.filters[f.key].length !== d[f.key].length || this.filters[f.key].some(v => !d[f.key].includes(v)));
    },
    filteredConstraints() {
      const sets = FILTERS.map(f => [f, new Set(this.filters[f.key])]);
      const clinicianOnly = this.scopeFilter === 'clinician';
      return this.constraints.filter(c => {
        if (clinicianOnly && !c.clinician_specific) return false;
        const solved = this.resolvedFor(c).length > 0;
        if (this.solvedFilter === 'yes' && !solved) return false;
        if (this.solvedFilter === 'no' && solved) return false;
        if (this.priorityFilter === 'yes' && !c.prioritized) return false;
        if (this.priorityFilter === 'no' && c.prioritized) return false;
        return sets.every(([f, set]) => set.has(f.value(c) || NONE));
      });
    },
    engTypeOptions() {
      const counts = {};
      for (const e of this.engagements) counts[e[2]] = (counts[e[2]] || 0) + 1;
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a]).map(t => ({ value: t, label: `${t} (${counts[t]})` }));
    },
    engPracticeSets() {
      const all = new Set();
      const pri = new Set();
      for (const c of this.filteredConstraints) {
        if (!c.practice_id) continue;
        all.add(c.practice_id);
        if (c.prioritized) pri.add(c.practice_id);
      }
      return { all, pri };
    },
    engInRange() {
      const start = isoDay(new Date(Date.now() - this.engRange * DAY_MS));
      const types = new Set(this.engTypes);
      return this.engagements.filter(e => e[1] >= start && types.has(e[2]));
    },
    engagementStats() {
      const { all, pri } = this.engPracticeSets;
      let allMeetings = 0;
      let priMeetings = 0;
      for (const e of this.engInRange) {
        if (all.has(e[0])) allMeetings++;
        if (pri.has(e[0])) priMeetings++;
      }
      const months = this.engRange / 30.44;
      return {
        allPractices: all.size, priPractices: pri.size, allMeetings, priMeetings,
        allPerPracticeMonth: all.size ? allMeetings / all.size / months : 0,
        priPerPracticeMonth: pri.size ? priMeetings / pri.size / months : 0,
      };
    },
    engagementChart() {
      const grain = this.engGrain;
      const today = new Date();
      const first = bucketStart(new Date(today.getTime() - this.engRange * DAY_MS), grain);
      const keys = [];
      const cur = new Date(first);
      while (cur <= today) {
        keys.push(isoDay(cur));
        if (grain === 'month') cur.setMonth(cur.getMonth() + 1);
        else cur.setDate(cur.getDate() + (grain === 'week' ? 7 : 1));
      }
      const idx = Object.fromEntries(keys.map((k, i) => [k, i]));
      const pri = keys.map(() => 0);
      const all = keys.map(() => 0);
      const { all: allSet, pri: priSet } = this.engPracticeSets;
      for (const e of this.engInRange) {
        const i = idx[isoDay(bucketStart(parseDay(e[1]), grain))];
        if (i == null) continue;
        if (allSet.has(e[0])) all[i]++;
        if (priSet.has(e[0])) pri[i]++;
      }
      const fmt = grain === 'month' ? { month: 'short', year: '2-digit' } : { month: 'short', day: 'numeric' };
      return {
        labels: keys.map(k => parseDay(k).toLocaleDateString('en-US', fmt)),
        series: [
          { values: all, config: { label: 'Practices with constraints in view', backgroundColor: '#60A5FA', type: 'bar', yAxis: 'left', offset: 0 } },
          { values: pri, config: { label: 'Practices with prioritized constraints', backgroundColor: '#F59E0B', type: 'bar', yAxis: 'left', offset: 1 } },
        ],
      };
    },
    engagementChartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 36, right: 24, bottom: 40, left: 48 },
        xAxis: { labelRotation: this.engGrain === 'day' ? -45 : 0, fontSize: 11 },
        yAxes: [{ position: 'left', min: 0, fontSize: 11, title: 'Engagements' }],
        legend: { display: true, position: 'top', spacing: 12 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        hideDataLabels: true,
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 11 },
      };
    },
    pageAllSelected() {
      return this.pageRows.length > 0 && this.pageRows.every(c => this.selectedIds.has(c.id));
    },
    pageSomeSelected() {
      return !this.pageAllSelected && this.pageRows.some(c => this.selectedIds.has(c.id));
    },
    modeledRows() {
      return this.filteredConstraints.filter(c => c.impact);
    },
    modeledCount() {
      return this.modeledRows.length;
    },
    testedImpactLabels() {
      return this.impactModel ? this.impactModel.initiatives.filter(i => i.basis === 'tested').map(i => i.label) : [];
    },
    impactBasisLabel() {
      const tested = this.testedImpactLabels;
      if (!tested.length) return 'Estimated';
      const allTested = tested.length === this.impactModel.initiatives.length;
      return allTested ? `${tested.join(', ')} tested` : `${tested.join(', ')} tested · others estimated`;
    },
    impactBasisTitle() {
      const tested = this.testedImpactLabels;
      if (!tested.length) return 'Every lift is an estimate.';
      if (tested.length === this.impactModel.initiatives.length) return 'Every lift is a tested blocker-clearance result.';
      return `${tested.join(', ')} lift is the tested blocker-clearance result; the other initiatives' lifts are estimates scaled from it.`;
    },
    impactLiftSummary() {
      if (!this.impactModel) return '';
      const specs = this.impactModel.initiatives;
      const tested = specs.filter(i => i.basis === 'tested')
        .map(i => `${i.label} ${(i.lift * 100).toFixed(1)} pts${i.evidence ? ` (tested: ${i.evidence})` : ' (tested)'}`);
      const estimated = specs.filter(i => i.basis !== 'tested').map(i => `${i.label} ${(i.lift * 100).toFixed(1)}`);
      const parts = [...tested];
      if (estimated.length) {
        const scaled = this.testedImpactLabels.length ? ` (estimates scaled from ${this.testedImpactLabels.join(', ')})` : ' (estimates)';
        parts.push(`${estimated.join('; ')}${scaled}`);
      }
      return `${parts.join('; ')}.`;
    },
    impactNotModeled() {
      if (!this.impactModel) return '';
      const modeled = new Set(this.impactModel.initiatives.map(i => i.initiative));
      const unmodeled = [...new Set(this.constraints.map(c => c.initiative).filter(k => k && !modeled.has(k)))]
        .sort().map(titleize);
      const items = [...unmodeled, 'constraints with no initiative', 'practices without a recent metric reading'];
      return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}.`;
    },
    impactCards() {
      if (!this.impactModel) return [];
      return this.impactModel.initiatives.map(spec => {
        const rows = this.modeledRows.filter(c => c.impact.initiative === spec.initiative);
        const total = rows.reduce((sum, c) => sum + c.impact.if_cleared, 0);
        const pri = rows.filter(c => c.prioritized);
        const priTotal = pri.reduce((sum, c) => sum + c.impact.if_cleared, 0);
        const dens = {};
        for (const c of rows) dens[c.practice_id] = c.impact.denominator;
        const practiceDen = Object.values(dens).reduce((a, b) => a + b, 0);
        return {
          ...spec, color: spec.color || '#6b7280', count: rows.length, practices: Object.keys(dens).length,
          rate: spec.network_rate, total, priCount: pri.length, priTotal,
          priPtsNetwork: spec.network_denominator ? (priTotal / spec.network_denominator) * 100 : 0,
          ptsNetwork: spec.network_denominator ? (total / spec.network_denominator) * 100 : 0,
          ptsPractices: practiceDen ? (total / practiceDen) * 100 : 0,
        };
      });
    },
    sortedConstraints() {
      const key = this.sortCol;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const val = (c) => {
        if (key === 'upcoming') return c.lapsed ? '0000-00-00' : (c.next_meeting || '9999-12-31');
        if (key === 'severity') return SEVERITY_RANK[c.severity] ?? 3;
        if (key === 'covered') return this.openActions(c);
        if (key === 'priority') return c.prioritized ? 1 : 0;
        if (key === 'attributed') return this.attributedOf(c) ?? -1;
        if (key === 'power') return c.solvability ? POWER_RANK[c.solvability.enabler_power] : 9;
        if (key === 'effort') return c.solvability ? EFFORT_RANK[c.solvability.effort] : 9;
        if (key === 'solved') return this.resolvedFor(c).length;
        return c[key] || '';
      };
      return [...this.filteredConstraints].sort((a, b) => {
        const x = val(a), y = val(b);
        const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
        return dir * cmp || (b.raised || '').localeCompare(a.raised || '');
      });
    },
    pageCount() { return Math.max(1, Math.ceil(this.filteredConstraints.length / PAGE_SIZE)); },
    pageStart() { return (this.page - 1) * PAGE_SIZE; },
    pageEnd() { return Math.min(this.pageStart + PAGE_SIZE, this.filteredConstraints.length); },
    pageRows() { return this.sortedConstraints.slice(this.pageStart, this.pageEnd); },
  },
  watch: {
    filters: { handler() { this.goPage(1); }, deep: true },
    scopeFilter() { this.goPage(1); },
    solvedFilter() { this.goPage(1); },
    priorityFilter() { this.goPage(1); },
    sortCol() { this.goPage(1); },
    sortDir() { this.goPage(1); },
  },
  async mounted() {
    if (this.embedded) { this.sortCol = 'upcoming'; this.sortDir = 'asc'; }
    if (this.embedded && this.entity_id == null) { this.loading = false; return; }
    try {
      const params = { group_by: 'constraint' };
      if (this.embedded) {
        params.scope_entity_id = this.entity_id;
        params.manager_id = this.entity_id;
      }
      const { data } = await axios.get('api/engagement-graph-nodes/ops-summary/', { params });
      this.constraints = data.constraints || [];
      this.impactModel = data.impact_model || null;
      this.engagements = data.engagements || [];
      this.engTypes = [...new Set(this.engagements.map(e => e[2]))];
      this.resetFilters();
      this.loadAttribution();
    } finally {
      this.loading = false;
    }
  },
  methods: {
    titleize, initialsOf, avatarColor,
    fmtDate(d) {
      if (!d) return '—';
      const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    attributedOf(c) {
      const v = this.attribution[c.practice_id];
      return v == null ? null : v;
    },
    async loadAttribution() {
      this.attributionLoading = true;
      try {
        const { data } = await axios.get('api/entities/attribution-breakdown/', { params: { grain_type: 'Practice', observation_year: new Date().getFullYear() } });
        const map = {};
        for (const r of (data.results || [])) map[r.entity_id] = r.attribution;
        this.attribution = map;
      } finally {
        this.attributionLoading = false;
      }
    },
    priorityTitle(n) {
      if (!n.prioritized) return 'Prioritize';
      return `Prioritized${n.prioritized_by ? ' by ' + n.prioritized_by : ''}${n.prioritized_at ? ' on ' + this.fmtDate(n.prioritized_at) : ''}. Click to clear.`;
    },
    async togglePriority(n) {
      const data = await setPriority(n.id, !n.prioritized);
      n.prioritized = data.prioritized_at != null;
      n.prioritized_at = data.prioritized_at;
      n.prioritized_by = data.prioritized_by_name;
    },
    toggleSelected(id) {
      const next = new Set(this.selectedIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      this.selectedIds = next;
    },
    togglePageSelection() {
      const next = new Set(this.selectedIds);
      if (this.pageAllSelected) this.pageRows.forEach(c => next.delete(c.id));
      else this.pageRows.forEach(c => next.add(c.id));
      this.selectedIds = next;
    },
    selectAllFiltered() {
      this.selectedIds = new Set(this.filteredConstraints.map(c => c.id));
    },
    clearSelection() {
      this.selectedIds = new Set();
    },
    async bulkPriority(prioritized) {
      this.bulkSaving = true;
      try {
        const data = await setPriorityBulk([...this.selectedIds], prioritized);
        const byId = new Map(data.updated.map(u => [u.id, u]));
        for (const c of this.constraints) {
          const u = byId.get(c.id);
          if (u) Object.assign(c, { prioritized: u.prioritized, prioritized_at: u.prioritized_at, prioritized_by: u.prioritized_by });
        }
        this.clearSelection();
      } finally {
        this.bulkSaving = false;
      }
    },
    openActions(c) {
      return (c.actions || []).filter(a => a.status === 'open' || a.status === 'in_progress').length;
    },
    actionsTitle(c) {
      const all = (c.actions || []).length;
      const open = this.openActions(c);
      return `${open} open · ${all - open} resolved`;
    },
    resolvedFor(c) {
      return c.solved_elsewhere || [];
    },
    applyEmbeddedSort(col) {
      this.sortCol = col;
      this.sortDir = ASC_DEFAULT.has(col) ? 'asc' : 'desc';
      this.expandedId = null;
    },
    nextMeetingLabel(c) {
      if (c.next_meeting) return `Mtg ${this.fmtDate(c.next_meeting)}`;
      if (c.lapsed) return c.last_meeting ? `Lapsed · last met ${this.fmtDate(c.last_meeting)}` : 'Lapsed · never met';
      return c.last_meeting ? `Last met ${this.fmtDate(c.last_meeting)}` : 'No meetings';
    },
    nextMeetingClass(c) {
      if (c.next_meeting) return 'text-blue-700';
      if (c.lapsed) return 'text-amber-700';
      return 'text-gray-400';
    },
    resetFilters() {
      this.filters = JSON.parse(JSON.stringify(this.defaultFilters));
      this.scopeFilter = 'all';
      this.solvedFilter = 'all';
      this.priorityFilter = 'all';
    },
    fmtInt(v) {
      return Math.round(v || 0).toLocaleString();
    },
    goPage(n) {
      this.page = Math.min(Math.max(1, n), this.pageCount);
      if (this.$refs.scroller) this.$refs.scroller.scrollTop = 0;
    },
    clickHeader(key) {
      if (this.sortCol === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
      else { this.sortCol = key; this.sortDir = ASC_DEFAULT.has(key) ? 'asc' : 'desc'; }
      this.expandedId = null;
    },
  },
};
</script>
